/**
 * Numeric-input normalization. Arabic is a first-class UI language here, so
 * users on Arabic/Persian keyboards naturally type Eastern-Arabic (٠١٢٣٤٥٦٧٨٩)
 * or Extended-Arabic/Persian (۰۱۲۳۴۵۶۷۸۹) digits. decimal.js (and Number)
 * only accept ASCII digits and THROW on anything else — which, unguarded, would
 * unmount the React tree. Every user-entered numeric string must pass through
 * here before it reaches a Decimal / Number, and every field should store the
 * normalized (ASCII) value so we also never POST non-ASCII digits to the API.
 */
import Decimal from 'decimal.js';

const AR_DIGIT_BASE = 0x0660; // ٠..٩  Eastern-Arabic
const FA_DIGIT_BASE = 0x06f0; // ۰..۹  Extended-Arabic / Persian

/**
 * Convert Eastern-Arabic (U+0660–U+0669) and Extended-Arabic / Persian
 * (U+06F0–U+06F9) digits to their ASCII (0–9) equivalents. Every other
 * character passes through unchanged.
 */
export function toAsciiDigits(input: string): string {
  let out = '';
  for (const ch of input) {
    const code = ch.codePointAt(0) ?? 0;
    if (code >= AR_DIGIT_BASE && code <= AR_DIGIT_BASE + 9) {
      out += String(code - AR_DIGIT_BASE);
    } else if (code >= FA_DIGIT_BASE && code <= FA_DIGIT_BASE + 9) {
      out += String(code - FA_DIGIT_BASE);
    } else {
      out += ch;
    }
  }
  return out;
}

/**
 * Normalize a raw numeric input string so decimal.js / Number can parse it.
 *
 * THOUSANDS SEPARATORS ARE REMOVED — including the ASCII comma. That one was
 * missing, and its absence was not cosmetic: decimal.js throws on "1,000", and
 * safeDecimal below catches the throw and returns its fallback, so pasting
 * "1,000" into a price silently booked ZERO. The Arabic separator was already
 * handled, so the app coped with ١٬٠٠٠ and not with the form a shop actually
 * types. Every document line, journal line and stock adjustment parses here.
 *
 * The decimal point is deliberately NOT touched: "10." is a legitimate halfway
 * state while somebody is still typing, and stripping it would make the decimal
 * un-typeable.
 *
 * Bidi marks are stripped for the same class of reason. Intl embeds them in
 * Arabic output — `ar-IQ` formats -1234 as "‎-1,234" — so a value that has
 * been through a formatter and back carries invisible characters that decimal.js
 * rejects. The Unicode minus and en-dash become ASCII "-" because a keyboard or
 * a spreadsheet paste can produce either.
 */
export function normalizeNumericInput(input: string): string {
  // Written as escapes, not the literal characters. Every one of these is
  // invisible in an editor, so spelled out they are unreviewable — and a stray
  // invisible character is the exact bug this function exists to absorb.
  return toAsciiDigits(input)
    // Bidi marks Intl embeds: LRM, RLM, ALM, and the four isolate controls.
    .replace(/[\u200E\u200F\u061C\u2066-\u2069]/g, '')
    .replace(/[\u2212\u2013]/g, '-') // Unicode minus / en-dash -> ASCII
    .replace(/\u066B/g, '.') // Arabic decimal separator
    // Thousands: ASCII comma, the Arabic one, thin space, narrow nbsp, nbsp.
    .replace(/[,\u066C\u2009\u202F\u00A0]/g, '');
}

/**
 * What a money box should hold after a keystroke or a paste.
 *
 * `normalizeNumericInput` treats every comma as a thousands mark and leaves the
 * Arabic comma alone, so a phone's decimal keypad — which types "٫" or "،" or
 * "," instead of "." on an Arabic or Kurdish locale — either threw (the amount
 * never parsed, the Record-payment button stayed dead) or, worse, turned "0,4"
 * into 4. Here the decimal separators are all folded to ".", and a comma only
 * counts as a thousands mark when it is followed by exactly three digits.
 * Digits are ASCII on the way out so what is shown is what is posted.
 */
export function normalizeAmountTyping(input: string): string {
  // Escapes, not literal characters: these are invisible in an editor.
  let s = toAsciiDigits(input)
    .replace(/[\u200E\u200F\u061C\u2066-\u2069]/g, '') // bidi marks
    .replace(/\u066B/g, '.') // Arabic decimal separator
    .replace(/[\u066C\u2009\u202F\u00A0\s]/g, '') // Arabic thousands, thin/narrow/no-break spaces
    .replace(/\u060C/g, ','); // Arabic comma

  // "1,000" / "1,000,000" are thousands; a lone comma with 1–4 digits after is a decimal point.
  s = s.replace(/,(?=\d{3}(?:\D|$))/g, '');
  s = s.replace(/,/g, '.');
  // Keep one decimal point only: "1.2.3" -> "1.23"
  const i = s.indexOf('.');
  if (i !== -1) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, '');
  return s;
}

/**
 * Build a Decimal from possibly-localized, possibly-partial user input WITHOUT
 * ever throwing. Digits are normalized to ASCII first; if the result still
 * isn't a finite number (e.g. "", "-", ".", "abc") the fallback (default 0) is
 * used — so a single bad keystroke can never crash the page (decimal.js throws
 * on invalid input).
 */
export function safeDecimal(
  value: string | number | null | undefined,
  fallback = 0,
): Decimal {
  const raw = typeof value === 'number' ? String(value) : normalizeNumericInput(value ?? '');
  try {
    const d = new Decimal(raw);
    return d.isFinite() ? d : new Decimal(fallback);
  } catch {
    return new Decimal(fallback);
  }
}
